import { Component, ElementRef, HostListener, OnInit, ViewChild } from '@angular/core';
import { ViewMode } from '../view-mode.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { Ech0134meldungsempfaengerService, MeldungsEmpfaengerDetailEntry } from '../../../core/grundbuchmeldung-api';
import { FormsModule, NgForm } from '@angular/forms';
import { markFormAsTouched } from '../../../shared/functions/form.functions';
import { Location } from '@angular/common';
import { NgbModal, NgbTooltip, NgbTypeahead, NgbTypeaheadSelectItemEvent } from '@ng-bootstrap/ng-bootstrap';
import { DirtyCheckModalComponent } from '../../../shared/components/modals/dirty-check-modal/dirty-check-modal.component';
import { PolitischeGemeindeListEntry, StammdatengemeindeService } from '../../../core/stammdaten-api';
import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs/operators';
import { deepCopy } from '../../../shared/functions/deep-copy.functions';
import { ComparatorService } from '../../../shared/services/comparator-service';
import { DeleteCheckModalComponent } from '../../../shared/components/modals/delete-check-modal/delete-check-modal.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CapAlertService } from '../../../core/extension/exceptions/cap-alert.service';
import { DirtyCheck } from '../../../shared/guards/dirty-check';
import { lastValueFrom, Observable, of } from 'rxjs';
import { OptimisticLockModalComponent } from '../../../shared/components/modals/optimistic-lock-modal/optimistic-lock-modal.component';
import { CapFormColumnsDirective } from '../../../shared/form-control/form-columns.directive';
import { ContentHeaderComponent } from '../../../shared/components/content-header/content-header.component';
import { TitleComponent } from '../../../shared/components/title/title.component';
import { TitleFunctionbarComponent } from '../../../shared/components/title-functionbar/title-functionbar.component';
import { CapFormRowDirective } from '../../../shared/form-control/cap-form-row.directive';
import { CapFormControlComponent } from '../../../shared/form-control/form-control.component';
import { AutofocusDirective } from '../../../shared/directives/autofocus.directive';
import { MetaDisablerDirective } from '../../../shared/directives/meta-disabler.directive';
import { MetaValidatorDirective } from '../../../shared/directives/meta-validator.directive';
import { RubrikComponent } from '../../../shared/components/rubrik/rubrik.component';
import { keysToTranslate } from '../../../../i18n/keys-to-translate';
import RegistriertAufEreignisseEnum = MeldungsEmpfaengerDetailEntry.RegistriertAufEreignisseEnum;
import SchemaversionEnum = MeldungsEmpfaengerDetailEntry.SchemaversionEnum;

@Component({
  selector: 'cap-meldungsempfaenger-detail',
  templateUrl: './meldungsempfaenger-detail.component.html',
  styleUrls: ['./meldungsempfaenger-detail.component.scss'],
  imports: [
    FormsModule,
    CapFormColumnsDirective,
    ContentHeaderComponent,
    TitleComponent,
    TitleFunctionbarComponent,
    NgbTooltip,
    CapFormRowDirective,
    CapFormControlComponent,
    AutofocusDirective,
    MetaDisablerDirective,
    MetaValidatorDirective,
    RubrikComponent,
    TranslatePipe,
    NgbTypeahead
  ]
})
export class MeldungsempfaengerDetailComponent implements OnInit, DirtyCheck {
  writeModel: MeldungsEmpfaengerDetailEntry;
  private viewMode: ViewMode;
  private readModel: MeldungsEmpfaengerDetailEntry;
  private nameComparator: (a: any, b: any) => number;
  private gemeindeSearchTerm = '';

  @ViewChild('form', { static: false })
  private form: NgForm;
  @ViewChild('bezeichnungInput', { static: false })
  private bezeichnungInput: ElementRef;
  @ViewChild('sedexIdInput', { static: false })
  private sedexIdInput: ElementRef;

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private backend: Ech0134meldungsempfaengerService,
    private modalService: NgbModal,
    private gemeindeService: StammdatengemeindeService,
    private comparatorService: ComparatorService,
    private translateService: TranslateService,
    private capAlertService: CapAlertService,
    private router: Router
  ) {
    keysToTranslate<string>([
      'meldungsempfaenger.bezeichnung',
      'meldungsempfaenger.bezeichnung.invalid',
      'meldungsempfaenger.sedexid',
      'meldungsempfaenger.sedexid.invalid'
    ]);
  }

  ngOnInit() {
    this.nameComparator = this.comparatorService.propertyComparator('name');
    this.viewMode = this.route.snapshot.data.viewMode;
    if (this.viewMode === ViewMode.NEW) {
      this.readModel = {
        meldungsEmpfaengerId: { id: '' },
        erhaeltMeldungen: true,
        erhaeltBasedelivery: true,
        registriertAufEreignisse: [],
        eingeschraenktAufGemeinden: []
      };
      this.writeModel = {
        meldungsEmpfaengerId: { id: '' },
        erhaeltMeldungen: true,
        erhaeltBasedelivery: true,
        registriertAufEreignisse: [],
        eingeschraenktAufGemeinden: [],
        schemaversion: SchemaversionEnum.ECH_0134_2_0
      };
    }
    if (this.viewMode === ViewMode.EDIT || this.viewMode === ViewMode.READ_ONLY) {
      this.route.snapshot.data.entity.eingeschraenktAufGemeinden.sort(this.nameComparator);
      this.readModel = deepCopy(this.route.snapshot.data.entity);
      this.writeModel = deepCopy(this.route.snapshot.data.entity);
    }
  }

  onSubmit() {
    markFormAsTouched(this.form);
    if (this.viewMode === ViewMode.NEW) {
      lastValueFrom(this.backend.createEmpfaenger(this.writeModel))
        .then((entity) => {
          entity.eingeschraenktAufGemeinden.sort(this.nameComparator);
          this.readModel = deepCopy(entity);
          this.writeModel = deepCopy(entity);
          this.viewMode = ViewMode.READ_ONLY;
        })
        .catch((reason) => {
          if (reason.status === 400 && reason.error && reason.error.exceptionName === 'AlreadyExistsException') {
            this.capAlertService.warning(this.translateService.instant('meldungsempfaenger.error.uniqueConstraint'));
            if (this.sedexIdInput) {
              this.form.controls['sedexid'].setErrors({ invalid: true });
              this.sedexIdInput.nativeElement.focus();
            }
          } else {
            throw reason;
          }
        });
    } else if (this.viewMode === ViewMode.EDIT) {
      lastValueFrom(this.backend.updateEmpfaenger(this.writeModel))
        .then(() => {
          lastValueFrom(this.backend.loadEmpfaenger(this.writeModel.meldungsEmpfaengerId.id)).then((entity) => {
            entity.eingeschraenktAufGemeinden.sort(this.nameComparator);
            this.readModel = deepCopy(entity);
            this.writeModel = deepCopy(entity);
            this.viewMode = ViewMode.READ_ONLY;
          });
        })
        .catch((reason) => {
          if (reason.status === 412) {
            this.modalService.open(OptimisticLockModalComponent).result.then(
              () => {
                // disable dirty checker
                this.resetModel();
                // reload same page
                const currentUrl = this.router.url;
                void this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
                  void this.router.navigate([currentUrl]);
                });
              },
              () => {
                // back to edit mode
              }
            );
          }
        });
    }
  }

  onEdit() {
    this.viewMode = ViewMode.EDIT;
    setTimeout(() => {
      if (this.bezeichnungInput) {
        this.bezeichnungInput.nativeElement.focus();
      }
    }, 100);
  }

  @HostListener('window:beforeunload', ['$event'])
  onBeforeunload($event) {
    if (this.checkDirty()) {
      $event.preventDefault();
      $event.returnValue = '';
    }
  }

  onCancel() {
    if (this.viewMode === ViewMode.NEW) {
      this.location.back();
      return;
    } else if (this.viewMode === ViewMode.EDIT && this.checkDirty()) {
      this.modalService.open(DirtyCheckModalComponent).result.then(
        () => {
          this.resetModel();
        },
        () => {}
      );
      return;
    }
    this.resetModel();
  }

  resetModel() {
    this.writeModel = deepCopy(this.readModel);
    this.viewMode = ViewMode.READ_ONLY;
  }

  checkDirty(): boolean {
    return (
      this.readModel.bezeichnung !== this.writeModel.bezeichnung ||
      this.readModel.sedexId !== this.writeModel.sedexId ||
      this.readModel.erhaeltBasedelivery !== this.writeModel.erhaeltBasedelivery ||
      this.readModel.erhaeltMeldungen !== this.writeModel.erhaeltMeldungen ||
      this.readModel.erhaeltVertraulicheDaten !== this.writeModel.erhaeltVertraulicheDaten ||
      this.checkArrayDirty(
        this.readModel.eingeschraenktAufGemeinden,
        this.writeModel.eingeschraenktAufGemeinden,
        (g1, g2) => g1.id.id === g2.id.id
      ) ||
      this.checkArrayDirty(this.readModel.registriertAufEreignisse, this.writeModel.registriertAufEreignisse, (e1, e2) => e1 === e2)
    );
  }

  checkArrayDirty<T>(a1: Array<T>, a2: Array<T>, equalFn: (a: T, b: T) => boolean): boolean {
    if (a1 === undefined || a2 === undefined || a1.length !== a2.length) {
      return true;
    }
    for (const obj1 of a1) {
      if (!a2.some((obj2) => equalFn(obj1, obj2))) {
        return true;
      }
    }
    for (const obj2 of a2) {
      if (!a1.some((obj1) => equalFn(obj1, obj2))) {
        return true;
      }
    }
    return false;
  }

  onDelete() {
    const modal = this.modalService.open(DeleteCheckModalComponent);
    modal.componentInstance.entity = this.writeModel.bezeichnung + ' - ' + this.writeModel.sedexId;
    modal.result.then(() => {
      lastValueFrom(this.backend.deleteEmpfaenger(this.writeModel.meldungsEmpfaengerId.id)).then(() => {
        this.location.back();
      });
    });
  }

  ereignisseEnums(): Array<RegistriertAufEreignisseEnum> {
    return Object.values(RegistriertAufEreignisseEnum);
  }

  translateEreigniss(e: RegistriertAufEreignisseEnum) {
    return this.translateService.instant('meldungsempfaenger.ereignisse.' + e);
  }

  isEditMode(): boolean {
    return this.viewMode === ViewMode.EDIT;
  }

  isNewMode(): boolean {
    return this.viewMode === ViewMode.NEW;
  }

  isReadOnlyMode(): boolean {
    return this.viewMode === ViewMode.READ_ONLY;
  }

  toggleEreignissCheckBox($event, ereigniss: RegistriertAufEreignisseEnum) {
    if ($event.target.checked) {
      this.writeModel.registriertAufEreignisse.push(ereigniss);
    } else {
      this.writeModel.registriertAufEreignisse.splice(this.writeModel.registriertAufEreignisse.indexOf(ereigniss), 1);
    }
  }

  toggleAllEreignissCheckBox($event) {
    if ($event.target.checked) {
      for (const e of this.ereignisseEnums()) {
        if (!this.writeModel.registriertAufEreignisse.includes(e)) {
          this.writeModel.registriertAufEreignisse.push(e);
        }
      }
    } else {
      this.writeModel.registriertAufEreignisse = [];
    }
  }

  removeGemeinde(gemeinde: PolitischeGemeindeListEntry) {
    this.writeModel.eingeschraenktAufGemeinden.splice(this.writeModel.eingeschraenktAufGemeinden.indexOf(gemeinde), 1);
  }

  onGemeindeSelect($event: NgbTypeaheadSelectItemEvent<PolitischeGemeindeListEntry>) {
    const item: PolitischeGemeindeListEntry = $event.item;
    if (item) {
      if (!this.writeModel.eingeschraenktAufGemeinden.find((dto) => dto.id.id === item.id.id)) {
        this.writeModel.eingeschraenktAufGemeinden.push(item);
        this.writeModel.eingeschraenktAufGemeinden.sort(this.nameComparator);
      }
    }
  }

  containsAllEreignisse(): boolean {
    for (const e of this.ereignisseEnums()) {
      if (!this.writeModel.registriertAufEreignisse.includes(e)) {
        return false;
      }
    }
    return true;
  }

  searchGemeinden = (search$: Observable<string>) => {
    return search$.pipe(
      debounceTime(100),
      distinctUntilChanged(),
      switchMap((searchTerm) => {
        this.gemeindeSearchTerm = searchTerm;
        if (searchTerm?.length >= 2) {
          return this.gemeindeService.loadGemeindeAll('GRUNDBUCHMELDUNG_REGISTER_SEARCH', 'NUR_GUELTIGE_GEMEINDEN', true);
        }
        return of([]);
      }),
      map((data: PolitischeGemeindeListEntry[]) => {
        const results = data.sort(this.nameComparator).filter(this.filterResults);
        return results;
      })
    );
  };

  filterResults = (result) => {
    return this.formatEntry(result).toLowerCase().includes(this.gemeindeSearchTerm.toLowerCase());
  };

  formatEntry(entry: PolitischeGemeindeListEntry): string {
    if (entry?.name && entry?.bfsNummer) {
      return entry.name + ' ' + entry.bfsNummer;
    }
    return '';
  }

  inputFormatter = (input: any) => {
    return '';
  };
  resultFormatter = (result: PolitischeGemeindeListEntry) => {
    if (result) {
      return this.formatEntry(result);
    }
    return '';
  };
}
